import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import useScrollLock from "../../../hooks/useScrollLock";
import "./Bill.css";

interface BillDialogProps {
  /** Spoken name of the dialog, e.g. "Bill for table 4". */
  ariaLabel: string;
  onClose: () => void;
  /** Show the corner close button. The receipt sheet has its own Done button. */
  showClose?: boolean;
  children: React.ReactNode;
}

/**
 * Overlay + dialog shell shared by every bill surface (kitchen review, diner
 * receipt): portal, Escape to close, click-outside to close and background
 * scroll lock. Content is passed in; this owns no bill data.
 */
const BillDialog: React.FC<BillDialogProps> = ({
  ariaLabel,
  onClose,
  showClose = false,
  children,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  useScrollLock(true, overlayRef);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="bill__overlay" ref={overlayRef} onClick={onClose} role="presentation">
      <div
        className="bill modal-scrollable"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {showClose && (
          <button type="button" className="bill__close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};

export default BillDialog;
